/**
 * CV date precision (audit C13: "dates retain original precision").
 *
 * A CV states its dates at the precision the author chose: "2019", "Mar 2019"
 * or an exact day. Parsed CV snapshots keep that as the string shape ("YYYY",
 * "YYYY-MM", "YYYY-MM-DD" / ISO instant). Career-record rows store a
 * `DateTime` plus a `*Precision` column ('year' | 'month' | 'day'; null for
 * rows written before precision was tracked, which read as month, exactly as
 * they always rendered).
 *
 * This module is the one place that turns either carrier into what a person
 * sees. Nothing here ever widens a date: a bare year prints as a year, never
 * as "Jan 2019"; a month prints as month + year; a day is never shown.
 *
 * Imported by the API, the mobile app, the web app and the native node:test
 * runner, so it has no sibling imports (see parsed-cv-to-overrides.ts).
 */

export type CvDatePrecision = "year" | "month" | "day";

export const CV_DATE_PRECISIONS: readonly CvDatePrecision[] = [
  "year",
  "month",
  "day",
];

export function isCvDatePrecision(value: unknown): value is CvDatePrecision {
  return (
    typeof value === "string" &&
    (CV_DATE_PRECISIONS as readonly string[]).includes(value)
  );
}

const YEAR_ONLY = /^\d{4}$/;
const YEAR_MONTH = /^\d{4}-\d{2}$/;
const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;
/** An ISO timestamp carrying its own offset, e.g. `2021-01-01T00:00:00.000Z`. */
const ISO_INSTANT =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/;
/** Same family of "still there" markers as `isOngoingDateMarker` (cv-validation). */
const ONGOING_MARKER =
  /^(?:present|presently|current|currently|now|ongoing|to[\s-]?date|till[\s-]?date|till[\s-]?now|to[\s-]?present|up\s?to\s?date)$/i;

/**
 * The precision a CV date STRING states, for callers that turn that string
 * into a `Date` with `new Date(value)` (HTTP controllers, agent tools):
 * "2019" is a year, "2019-03" a month, a calendar day or an ISO instant a
 * day. Free text ("Summer 2019", "Mar 2019") makes no claim: `null`, which
 * readers treat as month, today's behaviour.
 */
export function inferCvDatePrecision(
  value: string | null | undefined,
): CvDatePrecision | null {
  const trimmed = value?.trim();
  if (!trimmed || ONGOING_MARKER.test(trimmed)) return null;
  if (YEAR_ONLY.test(trimmed)) return "year";
  if (YEAR_MONTH.test(trimmed)) return "month";
  if (CALENDAR_DAY.test(trimmed) || ISO_INSTANT.test(trimmed)) return "day";
  return null;
}

function formatWithFallback(
  date: Date,
  locale: string,
  options: Intl.DateTimeFormatOptions,
): string {
  try {
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch {
    return new Intl.DateTimeFormat("en", options).format(date);
  }
}

/** Date's parser normalizes February 30 into March; a source never stated that month. */
function isValidCalendarDate(value: string): boolean {
  const [, year, month = "01", day = "01"] =
    /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/.exec(value) ?? [];
  if (!year) return false;
  const calendarDay = `${year}-${month}-${day}`;
  const parsed = new Date(`${calendarDay}T00:00:00.000Z`);
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === calendarDay
  );
}

/**
 * The label a CV prints for one date, in the reader's locale.
 *
 * - "YYYY" (a year the source stated alone) prints as the year only.
 * - Anything with a month ("YYYY-MM", a calendar day, an ISO instant, a
 *   `Date`) prints as short month + year, read in UTC because every stored
 *   instant is UTC midnight (a local read west of Greenwich would print the
 *   previous month).
 * - Empty values, ongoing markers ("Present") and unreadable text print
 *   nothing: the caller decides what an absent date means.
 */
export function formatCvDateLabel(
  value: string | Date | null | undefined,
  locale: string = "en",
): string {
  if (!value) return "";
  let date: Date;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || ONGOING_MARKER.test(trimmed)) return "";
    if (YEAR_ONLY.test(trimmed)) {
      // Date.UTC treats years 0..99 as 1900..1999. Localize the stated digits
      // directly so no century or calendar era is invented.
      try {
        return new Intl.NumberFormat(locale, {
          useGrouping: false,
          minimumIntegerDigits: 4,
        }).format(Number(trimmed));
      } catch {
        return trimmed;
      }
    }
    if (
      (YEAR_MONTH.test(trimmed) || CALENDAR_DAY.test(trimmed)) &&
      !isValidCalendarDate(trimmed)
    ) {
      return "";
    }
    if (ISO_INSTANT.test(trimmed) && !isValidCalendarDate(trimmed.slice(0, 10))) {
      return "";
    }
    date = new Date(trimmed);
  } else {
    date = value;
  }
  if (Number.isNaN(date.getTime())) return "";
  return formatWithFallback(date, locale, {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function utcYear(date: Date): string {
  return String(date.getUTCFullYear()).padStart(4, "0");
}

function utcYearMonth(date: Date): string {
  return `${utcYear(date)}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

/**
 * A career-record row's date as the resolved document carries it. A
 * year-precision row becomes the bare year (the same shape an imported CV
 * snapshot already uses, so every reader handles it); month rows use
 * YYYY-MM so a subsequent editor/tool save cannot infer an invented day from
 * a serialized ISO instant. Day and legacy rows keep their ISO instant.
 */
export function serializeProfileDate(
  date: Date | null | undefined,
  precision: string | null | undefined,
): string | null {
  if (!date) return null;
  if (precision === "year") return utcYear(date);
  if (precision === "month") return utcYearMonth(date);
  return date.toISOString();
}

/**
 * A career-record row's date as the one-step import backup (`ResumeSource.
 * previousRaw`) records it, so a revert restores the row at the precision it
 * had. Rows from before precision was tracked keep the calendar-day shape the
 * backup always wrote.
 */
export function formatProfileDateForBackup(
  date: Date,
  precision: string | null | undefined,
): string {
  if (precision === "year") return utcYear(date);
  if (precision === "month") return utcYearMonth(date);
  return date.toISOString().slice(0, 10);
}
